import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'api';
import { WildfireListItem } from 'features/wildfires/wildfire.types';
import { LocationListItem } from 'features/locations/location.types';
import { HazardListItem } from 'features/hazards/hazard.types';
import { useCacheState } from 'state/useCacheState';
import { useAuthState } from 'state/useAuthState';
import { MAIN_REFETCH_INTERVAL_MS } from 'features/mapping/mapping.constants';
import { FloodingListItem } from 'features/flooding/flooding.types';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { parseGeoEventDates } from 'features/geoEvents/geoEvent.utils';
import { WithStringDates } from 'shared/types';

export type UseGeoEventsProps = {
  enabled?: boolean;
};

type UseGeoEvents = {
  allGeoEvents: (
    | WildfireListItem
    | LocationListItem
    | FloodingListItem
    | HazardListItem
    | undefined
  )[];
  wildfireEvents: WildfireListItem[];
  locations: LocationListItem[];
  floodingEvents: FloodingListItem[];
  hazards: HazardListItem[];
};

type GeoEventResponseList = Array<
  WildfireListItem | LocationListItem | FloodingListItem | HazardListItem
>;

const GEO_EVENTS_URL = 'geo_events/';

const getStaleTime = (canReport: boolean): number | undefined => {
  // don't cache anything for reporters on the main map
  if (canReport) {
    return undefined;
  }
  return MAIN_REFETCH_INTERVAL_MS;
};

const getRefetchInterval = (
  canReport: boolean,
  hasSelectedGeoEvent: boolean,
): number | false => {
  if (canReport) {
    // 30 seconds
    return 30 * 1000;
  }
  if (hasSelectedGeoEvent) {
    return false;
  }
  return MAIN_REFETCH_INTERVAL_MS;
};

const fetchGeoEvents = async (
  cacheBusterTs: number | null,
): Promise<AxiosResponse<GeoEventResponseList>> => {
  const geoEventTypes = ['wildfire', 'location', 'flooding', 'hazard'];
  const params = {
    geoEventTypes: geoEventTypes.join(','),
    ...(cacheBusterTs ? { ts: cacheBusterTs } : {}),
  };
  const res = await API.get<WithStringDates<GeoEventResponseList[number]>[]>(
    GEO_EVENTS_URL,
    { params },
  );
  return {
    ...res,
    // Re-narrow: parseGeoEventDates widens the union to its base date constraint.
    data: res.data.map(
      (geoEvent) =>
        parseGeoEventDates(geoEvent) as GeoEventResponseList[number],
    ),
  };
};

export const useAllGeoEvents = (
  props: UseGeoEventsProps = {},
): UseGeoEvents => {
  const { enabled = true } = props;
  const { cacheBusterTs } = useCacheState();
  const {
    permissions: { canReportWildfire, canReportFlooding, canReportHazard },
  } = useAuthState();
  const selectedGeoEventId = useSelectedGeoEventId();

  const canReportAny =
    canReportWildfire || canReportFlooding || canReportHazard;

  const { data: results } = useQuery({
    queryKey: ['geoEvents', cacheBusterTs],
    queryFn: () => fetchGeoEvents(cacheBusterTs),
    staleTime: getStaleTime(canReportAny),
    refetchInterval: () =>
      getRefetchInterval(canReportAny, Boolean(selectedGeoEventId)),
    enabled,
  });

  const data: (
    | WildfireListItem
    | LocationListItem
    | FloodingListItem
    | HazardListItem
  )[] = useMemo(() => results?.data ?? [], [results]);

  const { wildfireEvents, locations, floodingEvents, hazards } = useMemo(() => {
    const fireEvents = data.filter(
      (item): item is WildfireListItem => item?.geoEventType === 'wildfire',
    );

    const locationEvents = data.filter(
      (item): item is LocationListItem => item?.geoEventType === 'location',
    );

    const floodEvents = data.filter(
      (item): item is FloodingListItem => item?.geoEventType === 'flooding',
    );

    const hazardEvents = data.filter(
      (item): item is HazardListItem => item?.geoEventType === 'hazard',
    );

    return {
      wildfireEvents: fireEvents,
      locations: locationEvents,
      floodingEvents: floodEvents,
      hazards: hazardEvents,
    };
  }, [data]);

  return {
    allGeoEvents: data,
    wildfireEvents,
    locations,
    floodingEvents,
    hazards,
  };
};
